import { Button } from '@hologram-dimension/button';
import { Panel } from '@hologram-dimension/panel';
import useDeviceWebhook from 'api/legacy/devices/useDeviceWebhook';
import Field from 'components/Field/Field';
import { type FC, useCallback, useEffect, useState } from 'react';
import useOrganization from 'state/contexts/Organization/useOrganization';
import useRegenerateWebhook from 'api/legacy/devices/useRegenerateWebhook';
import { BannerNotification } from '@hologram-dimension/banner-notification';
import useDeleteWebhook from 'api/legacy/devices/useDeleteWebhook';
import UpdateWebhookForm from './UpdateWebhookForm';
import styles from './WebhookPanel.module.css';

interface WebhookFormProps {
  deviceId?: DeviceId;
}

const WebhookForm: FC<WebhookFormProps> = ({ deviceId }) => {
  const organizationId = useOrganization();
  const {
    deviceWebhook,
    isLoading: deviceWebhookIsLoading,
    isValidating: deviceWebhookIsValidating,
    mutate: mutateDeviceWebhook,
  } = useDeviceWebhook(deviceId, organizationId);

  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const copyUrlToClipboard = useCallback(() => {
    if (!deviceWebhook?.url) {
      return;
    }
    navigator.clipboard.writeText(deviceWebhook.url);
    setCopiedToClipboard(true);
  }, [deviceWebhook.url]);

  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasWebhook = deviceWebhook.url && deviceWebhook.url.length > 0;

  useEffect(() => {
    if (deviceWebhook?.url) {
      setCopiedToClipboard(false);
    }
  }, [deviceWebhook?.url]);

  const [regenerating, setRegenerating] = useState(false);
  const regenerateApi = useRegenerateWebhook({ deviceId });
  const regenerateWebhook = useCallback(async () => {
    setRegenerating(true);
    setError(null);
    let result;
    try {
      result = await regenerateApi();
    } catch (apiError) {
      setError(`${apiError}`);
    }

    if (result?.success && result?.data) {
      mutateDeviceWebhook({ data: result.data, success: true }, { revalidate: true });
    }
    setRegenerating(false);
  }, [mutateDeviceWebhook, regenerateApi]);

  const [deleting, setDeleting] = useState(false);
  const deleteApi = useDeleteWebhook({ deviceId });
  const deleteWebhook = useCallback(async () => {
    setDeleting(true);
    setError(null);
    let result;
    try {
      result = await deleteApi();
    } catch (apiError) {
      setError(`${apiError}`);
    }

    if (result?.success) {
      mutateDeviceWebhook({ data: {} as any, success: true }, { revalidate: true });
    }
    setDeleting(false);
  }, [mutateDeviceWebhook, deleteApi]);

  let content;
  if (!deviceId) {
    content = null;
  } else if (isEditing) {
    content = (
      <UpdateWebhookForm
        deviceId={deviceId}
        onDone={() => {
          setIsEditing(false);
          mutateDeviceWebhook(undefined, { revalidate: true });
        }}
      />
    );
  } else if (!deviceWebhookIsLoading && hasWebhook) {
    content = (
      <>
        <Field label="Port" value={deviceWebhook.port} />
        <Field label="Protocol" value={deviceWebhook.protocol} />
      </>
    );
  } else {
    content = (
      <Field
        label="Setup"
        value={
          <Button
            onClick={() => {
              setIsEditing(true);
            }}
            variant="secondary"
          >
            Generate webhook
          </Button>
        }
      />
    );
  }

  return (
    <Panel
      header="Configure inbound webhook"
      contentWell="Send messages directly to your device by issuing a POST request to a sharable endpoint."
      isLoading={!deviceId || deviceWebhookIsLoading || deviceWebhookIsValidating}
      footerActions={
        hasWebhook && !isEditing ? (
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setIsEditing(true);
              }}
            >
              Update
            </Button>
            <Button variant="secondary" loading={regenerating} onClick={regenerateWebhook}>
              Regenerate
            </Button>
            <Button variant="delete" loading={deleting} onClick={deleteWebhook}>
              Delete
            </Button>
          </>
        ) : undefined
      }
    >
      {error && <BannerNotification variant="error">{error}</BannerNotification>}
      <div className={styles.webhook}>
        {hasWebhook && (
          <Field
            label="URL"
            value={
              <div className={styles.url}>
                <span>{deviceWebhook.url}</span>
                <Button
                  iconStart={copiedToClipboard ? 'CheckmarkSingle' : 'Document'}
                  onClick={copyUrlToClipboard}
                  variant="secondary"
                />
              </div>
            }
          />
        )}
        {content}
      </div>
    </Panel>
  );
};

export default WebhookForm;
